import {
  useCreateEmployee,
  useDeleteEmployee,
  useGetEmployees,
  useUpdateEmployee,
} from '@/api/user.api';
import useTableControls from '@/hooks/useTableControls';
import { Employee, employeeColumns } from '@/types/profile/employeeColumnDef';
import { RowSelectionState } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import StyledTable from '../styled/StyledTable';
import StyledNextPreviousButtons from '../styled/StyledNextPreviousButtons';
import Modal from '../ui/Modal';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  EmployeeFormData,
  EmployeeSchema,
} from '@/types/profile/EmployeeFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '@/hooks/useToast';
import StyledInput from '../styled/StyledInput';
import ModalDeletion from '../ui/ModalDeletion';
import {
  UpdateEmployeeFormData,
  UpdateEmployeeSchema,
} from '@/types/profile/UpdateEmployeeFormData';
import { AxiosError } from 'axios';
import { PAGE_SIZE } from '@/constants/pageSize';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { toUpdateEmployeeFormValues } from '@/utils/profile/toUpdateEmployeeFormValues';
import { toastFirstFormError } from '@/utils/form/toastFirstFormError';

interface DistributorProfileEmployeesTabProps {
  setSelected: (Ids: string[]) => void;
}

const DistributorProfileEmployeesTab = ({
  setSelected,
}: DistributorProfileEmployeesTabProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();
  const { data: employeesResponse, isSuccess } = useGetEmployees({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
  });
  const { mutateAsync: deleteEmployee } = useDeleteEmployee();

  const { toastAsync } = useToast();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [employeesToDeleteId, setEmployeesToDeleteId] = useState<string[]>([]);
  const [employeeToUpdate, setEmployeeToUpdate] = useState<Employee | null>(
    null,
  );

  const selectedEmployees = useMemo(
    () => Object.keys(rowSelection),
    [rowSelection],
  );

  useEffect(() => {
    setSelected(selectedEmployees);
  }, [selectedEmployees, setSelected]);

  return (
    <>
      <div className="flex flex-col w-full h-auto p-6 gap-6">
        <StyledTable
          columns={getTranslatedConst(employeeColumns)}
          data={employeesResponse?.employees ?? []}
          sorting={sorting}
          setSorting={setSorting}
          getRowId={(row) => row.id}
          setSelection={setRowSelection}
          onEditRow={(row) => {
            setEmployeeToUpdate(row.original);
          }}
        />
        {isSuccess && (
          <StyledNextPreviousButtons
            page={page}
            pageSize={PAGE_SIZE}
            setPage={setPage}
            dataLength={employeesResponse.total}
            label={t('pages.profile.user')}
          />
        )}
      </div>
      {employeesToDeleteId.length > 0 && (
        <ModalDeletion
          type="user"
          actionOnClick={() => {
            if (employeesToDeleteId.length === 1)
              toastAsync(deleteEmployee(employeesToDeleteId[0]), {
                success: t('pages.profile.userDeletedSuccess'),
              });
          }}
          closeModal={() => {
            setEmployeesToDeleteId([]);
          }}
        />
      )}
      {employeeToUpdate && (
        <DistributorUpdateEmployeeModal
          closeModal={() => {
            setEmployeeToUpdate(null);
          }}
          employee={employeeToUpdate}
        />
      )}
    </>
  );
};

export default DistributorProfileEmployeesTab;

export const DistributorProfileEmployeeAdditionModal = ({
  closeModal,
}: {
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  const methods = useForm<EmployeeFormData>({
    resolver: zodResolver(EmployeeSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      email: undefined,
      password: undefined,
      phoneNumber: undefined,
    },
  });

  const { control, handleSubmit } = methods;

  const { mutateAsync: createEmployee } = useCreateEmployee();

  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<EmployeeFormData> = async (data) => {
    toastAsync(createEmployee(data), {
      success: t('pages.profile.employee.addSuccess'),
      error: {
        render: ({ data }) => {
          if (data instanceof AxiosError && data.status === 409)
            return t('pages.profile.employee.emailAlreadyUsed');
          return t('pages.profile.employee.error');
        },
      },
    });
  };

  return (
    <Modal
      title={t('pages.profile.employee.add')}
      message={t('pages.profile.employee.addMessage')}
      actionTitle={t('pages.profile.employee.addButton')}
      actionOnClick={handleSubmit(submitForm)}
      closeModal={closeModal}
      isActionDisabled={!methods.formState.isValid}
    >
      <div className="flex flex-col gap-4">
        <StyledInput
          control={control}
          title={t('pages.profile.employee.lastName')}
          name="lastName"
          placeholder={t('pages.profile.employee.enterLastName')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.firstName')}
          name="firstName"
          placeholder={t('pages.profile.employee.enterFirstName')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.email')}
          name="email"
          type="email"
          placeholder={t('pages.profile.employee.enterEmail')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.password')}
          name="password"
          type="password"
          placeholder={t('pages.profile.employee.enterPassword')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.phoneNumber')}
          name="phoneNumber"
          type="tel"
          placeholder={t('pages.profile.employee.enterPhoneNumber')}
        />
      </div>
    </Modal>
  );
};

const DistributorUpdateEmployeeModal = ({
  closeModal,
  employee,
}: {
  closeModal: () => void;
  employee: Employee;
}) => {
  const { t } = useTranslation();

  const methods = useForm<UpdateEmployeeFormData>({
    resolver: zodResolver(UpdateEmployeeSchema),
    mode: 'onChange',
    defaultValues: toUpdateEmployeeFormValues(employee),
  });

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
  } = methods;

  const { mutateAsync: updateEmployee } = useUpdateEmployee();

  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<UpdateEmployeeFormData> = async (data) => {
    toastAsync(
      updateEmployee({
        employeeId: employee.id,
        employeeData: {
          firstName: dirtyFields.firstName ? data.firstName : undefined,
          lastName: dirtyFields.lastName ? data.lastName : undefined,
          email: dirtyFields.email ? data.email : undefined,
          password: dirtyFields.password ? data.password : undefined,
          phoneNumber: dirtyFields.phoneNumber ? data.phoneNumber : undefined,
        },
      }),
      {
        success: t('pages.profile.userUpdatedSuccess'),
      },
    );
    closeModal();
  };

  return (
    <Modal
      title={t('pages.profile.employee.modify')}
      message={t('pages.profile.employee.modifyMessage')}
      actionTitle={t('pages.profile.employee.modify')}
      actionOnClick={handleSubmit(submitForm, (errors) =>
        toastFirstFormError(errors, t('common.checkRequiredFields')),
      )}
      closeModal={closeModal}
      shouldCloseModalAfterAction={false}
    >
      <div className="flex flex-col gap-4">
        <StyledInput
          control={control}
          title={t('pages.profile.employee.lastName')}
          name="lastName"
          placeholder={t('pages.profile.employee.enterLastName')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.firstName')}
          name="firstName"
          placeholder={t('pages.profile.employee.enterFirstName')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.email')}
          name="email"
          type="email"
          placeholder={t('pages.profile.employee.enterEmail')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.password')}
          name="password"
          type="password"
          placeholder={t('pages.profile.employee.enterPassword')}
        />
        <StyledInput
          control={control}
          title={t('pages.profile.employee.phoneNumber')}
          name="phoneNumber"
          type="tel"
          placeholder={t('pages.profile.employee.enterPhoneNumber')}
        />
      </div>
    </Modal>
  );
};
